import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Play } from "lucide-react";
import { TriviaBottomNav } from "./Trivia";
import { TV, TvKicker, TvHeader } from "../components/TriviaTheme";
import { useVisitor } from "../context/VisitorAuthContext";

const KINDS = [
  { id: "daily", label: "Daily Three", url: (d) => `/trivia/play?source=daily&date=${d}&mode=mc&group=0`, blurb: "Replay any of the last 14 Daily Threes." },
  { id: "chart-uk", label: "UK Chart Three", url: (d) => `/trivia/play?source=chart-daily&region=uk&date=${d}&mode=mc&count=3`, blurb: "Replay any of the last 14 UK Chart Threes." },
  { id: "chart-us", label: "US Chart Three", url: (d) => `/trivia/play?source=chart-daily&region=us&date=${d}&mode=mc&count=3`, blurb: "Replay any of the last 14 US Chart Threes." },
  { id: "sport", label: "Sport Three", url: (d) => `/trivia/play?source=sport-daily&date=${d}&mode=mc&count=3`, blurb: "Replay any of the last 14 Sport Threes." },
];

export default function TriviaArchive() {
  const { isMember } = useVisitor();
  const [kind, setKind] = useState(KINDS[0]);
  const days = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() - i);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, []);

  const label = (iso) =>
    new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });

  return (
    <Layout>
      <div className={`font-ui ${TV.bg} min-h-screen`}>
        <TvHeader title="Archive" subtitle="Back to the Trivia Vault" isMember={isMember} back="/trivia" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-24 sm:pb-16">
          <TvKicker className="tv-rise">For: catching up</TvKicker>
          <h1 className="tv-rise font-tv-serif font-bold text-[34px] sm:text-5xl text-[#161514] leading-[1.02] mt-4">Daily archive.</h1>
          <p className="tv-rise font-ui text-[15px] sm:text-lg text-[#77716A] mt-3 leading-relaxed" data-testid="archive-blurb">{kind.blurb} Each day's set is fixed, so you get the exact same questions.</p>
          <div className="flex flex-wrap gap-1.5 mt-6" data-testid="archive-tabs">
            {KINDS.map((k) => (
              <button key={k.id} data-testid={`archive-tab-${k.id}`} onClick={() => setKind(k)}
                className={`px-4 py-1.5 rounded-full font-ui text-xs sm:text-sm font-semibold border transition-[background-color,color] ${kind.id === k.id ? "bg-[#161514] text-white border-[#161514]" : "bg-[#EEE8DD] text-[#4A453E] border-[#DDD5C7] hover:bg-[#E6DFD2]"}`}>
                {k.label}
              </button>
            ))}
          </div>

          <div className={`${TV.card} mt-4 overflow-hidden`} data-testid="archive-list">
            {days.map((iso, i) => (
              <Link
                key={iso}
                data-testid={`archive-day-${i}`}
                to={kind.url(iso)}
                className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-3 hover:bg-[#F8F5EF] transition-colors ${i ? "border-t border-[#ECE6DB]" : ""}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className={`${i === 0 ? TV.pillMint : TV.pillTag} font-ui text-[11px] font-semibold px-2.5 py-1 shrink-0 w-[58px] text-center`}>{i === 0 ? "Today" : `−${i}d`}</span>
                  <span className="font-ui text-sm sm:text-base font-semibold text-[#161514] truncate">{label(iso)}</span>
                </div>
                <span className={`${i === 0 ? "bg-[#2D4A3E] text-white" : "bg-[#161514] text-white"} rounded-full inline-flex items-center gap-1 px-3 py-1.5 font-ui text-xs font-semibold shrink-0`}>
                  <Play className="w-3 h-3" fill="currentColor" /> Play
                </span>
              </Link>
            ))}
            <div className={`${TV.footer} px-4 sm:px-6 py-2.5 flex justify-between font-ui text-[11px] sm:text-xs text-[#77716A]`}>
              <span>Today: free</span><span>Past days: Club</span>
            </div>
          </div>
        </div>
        <TriviaBottomNav active="archive" />
      </div>
    </Layout>
  );
}
